import React, { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Mountain, Map as MapIcon } from "lucide-react";
import i18n from "../i18n";

/**
 * VenueMap — mapa 3D con MapLibre GL + tiles gratis OpenFreeMap (Liberty).
 * Estilo oscuro, bloques/edificios en 3D y pines con la silueta de la cabeza Kiltro
 * (amarillo eléctrico, sin círculo ni fondo blanco).
 */
const SANTIAGO_CENTER = [-70.6383, -33.4378];
// Estilo vector OpenFreeMap Liberty (gratis, sin API key). Sobre este estilo
// aplicamos override dark + 3D buildings (fill-extrusion) al cargar.
const MAP_STYLE = "https://tiles.openfreemap.org/styles/liberty";
const KILTRO_YELLOW = "#FEBA01"; // amarillo eléctrico oficial

export default function VenueMap({
  venues = [],
  height = 480,
  numbered = false,
  showLine = false,
  center,
  zoom = 12.5,
}) {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [is3D, setIs3D] = useState(true);

  const toggleView = () => {
    const map = mapRef.current;
    if (!map) return;
    const next = !is3D;
    setIs3D(next);
    map.easeTo({
      pitch: next ? 55 : 0,
      bearing: next ? -17 : 0,
      duration: 700,
    });
  };

  const computeCenter = () => {
    if (center) {
      if (center[0] < 0 && center[0] > -60 && center[1] < -60) {
        return [center[1], center[0]];
      }
      return center;
    }
    if (venues[0]) return [venues[0].lng, venues[0].lat];
    return SANTIAGO_CENTER;
  };

  // Mount map once
  useEffect(() => {
    if (mapRef.current || !mapContainer.current) return;

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: MAP_STYLE,
      center: computeCenter(),
      zoom,
      pitch: 55,
      bearing: -17,
      attributionControl: false,
      pitchWithRotate: true,
      dragRotate: true,
      antialias: true,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: true, visualizePitch: true }), "top-right");
    map.addControl(
      new maplibregl.AttributionControl({ compact: true, customAttribution: "© OpenFreeMap · OpenStreetMap" }),
      "bottom-right"
    );

    // Al cargar estilo vectorial: re-tintar dark + agregar 3D buildings
    map.on("style.load", () => {
      applyDarkTheme(map);
      add3DBuildings(map);
    });

    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update markers + route line when venues change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const setupMarkers = () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];

      venues.forEach((v, i) => {
        if (!v.lat || !v.lng) return;
        const el = document.createElement("div");
        el.className = "kiltro-maplibre-pin";
        el.innerHTML = markerHtml(numbered ? i + 1 : null);

        const popup = new maplibregl.Popup({
          offset: 52,
          closeButton: true,
          closeOnClick: true,
          maxWidth: "300px",
          className: "kiltro-maplibre-popup",
        }).setHTML(popupHtml(v, numbered ? i + 1 : null));

        const marker = new maplibregl.Marker({ element: el, anchor: "bottom" })
          .setLngLat([v.lng, v.lat])
          .setPopup(popup)
          .addTo(map);
        markersRef.current.push(marker);
      });

      if (venues.length > 1) {
        const bounds = new maplibregl.LngLatBounds();
        venues.forEach((v) => v.lat && v.lng && bounds.extend([v.lng, v.lat]));
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 900 });
        }
      } else if (venues.length === 1 && venues[0].lat && venues[0].lng) {
        map.flyTo({ center: [venues[0].lng, venues[0].lat], zoom: 16.5, duration: 900 });
      }

      // Route line
      const SRC = "kiltro-route";
      const LYR = "kiltro-route-line";
      if (map.getLayer(LYR)) map.removeLayer(LYR);
      if (map.getSource(SRC)) map.removeSource(SRC);

      if (showLine && venues.length > 1) {
        map.addSource(SRC, {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: venues.filter((v) => v.lat && v.lng).map((v) => [v.lng, v.lat]),
            },
          },
        });
        map.addLayer({
          id: LYR,
          type: "line",
          source: SRC,
          paint: {
            "line-color": KILTRO_YELLOW,
            "line-width": 3.5,
            "line-dasharray": [2, 2],
            "line-opacity": 0.95,
          },
          layout: { "line-cap": "round", "line-join": "round" },
        });
      }
    };

    if (map.loaded() && map.isStyleLoaded()) {
      setupMarkers();
    } else {
      map.once("load", setupMarkers);
    }
  }, [venues, numbered, showLine]);

  return (
    <div
      className="w-full border-2 border-[#1A1A1A] overflow-hidden bg-black relative kiltro-map-3d"
      style={{ height }}
      data-testid="venue-map-wrapper"
    >
      <div ref={mapContainer} className="w-full h-full" data-testid="venue-map" />
      <button
        type="button"
        onClick={toggleView}
        data-testid="map-view-toggle"
        className="absolute top-3 left-3 z-10 inline-flex items-center gap-2 bg-black/85 border-2 border-[#FEBA01] text-[#FEBA01] hover:bg-[#FEBA01] hover:text-black transition-colors px-3 py-2 font-display uppercase tracking-tight text-xs"
        style={{ boxShadow: "2px 2px 0 0 rgba(0,0,0,0.9)" }}
        aria-label={is3D ? "Cambiar a vista 2D" : "Cambiar a vista 3D"}
        title={is3D ? "Vista 2D plana" : "Vista 3D inmersiva"}
      >
        {is3D ? <MapIcon size={14} /> : <Mountain size={14} />}
        {is3D ? "Vista 2D" : "Vista 3D"}
      </button>
    </div>
  );
}

// Dark Kiltro theme: overrides en vivo sobre el style vector Liberty ----------
function applyDarkTheme(map) {
  try {
    const style = map.getStyle();
    if (!style || !style.layers) return;
    style.layers.forEach((layer) => {
      const id = layer.id;
      const type = layer.type;
      if (type === "background") {
        try { map.setPaintProperty(id, "background-color", "#050505"); } catch (_e) {}
      } else if (type === "fill") {
        let color = "#0C0C0C";
        if (id.includes("water")) color = "#030308";
        else if (id.includes("park") || id.includes("wood") || id.includes("green") || id.includes("grass")) color = "#0D120C";
        else if (id.includes("building")) color = "#161616";
        else if (id.includes("sand") || id.includes("beach")) color = "#12110A";
        else if (id.includes("landuse") || id.includes("residential") || id.includes("commercial")) color = "#0A0A0A";
        try { map.setPaintProperty(id, "fill-color", color); } catch (_e) {}
        try { map.setPaintProperty(id, "fill-opacity", 0.92); } catch (_e) {}
      } else if (type === "line") {
        let color = "#181818";
        let opacity = 0.8;
        if (id.includes("motorway") || id.includes("trunk")) { color = "#2E2E2E"; opacity = 0.95; }
        else if (id.includes("primary") || id.includes("secondary")) { color = "#232323"; opacity = 0.9; }
        else if (id.includes("road") || id.includes("street") || id.includes("service") || id.includes("highway")) color = "#1A1A1A";
        else if (id.includes("rail")) color = "#1E1E1E";
        else if (id.includes("boundary") || id.includes("admin")) { color = "#2A2016"; opacity = 0.4; }
        try { map.setPaintProperty(id, "line-color", color); } catch (_e) {}
        try { map.setPaintProperty(id, "line-opacity", opacity); } catch (_e) {}
      } else if (type === "symbol") {
        // Ocultar POIs con ícono (restaurantes, parking, etc.) para que sólo se vean
        // nuestros pines Kiltro. Mantener text labels (calles, barrios).
        const hasIcon = layer.layout && layer.layout["icon-image"];
        const hasText = layer.layout && layer.layout["text-field"];
        if (hasIcon && !hasText) {
          try { map.setLayoutProperty(id, "visibility", "none"); } catch (_e) {}
          return;
        }
        if (hasIcon && hasText) {
          // Quitar el icono pero dejar el texto
          try { map.setLayoutProperty(id, "icon-image", ""); } catch (_e) {}
        }
        try { map.setPaintProperty(id, "text-color", "#7A7A7A"); } catch (_e) {}
        try { map.setPaintProperty(id, "text-halo-color", "#000000"); } catch (_e) {}
        try { map.setPaintProperty(id, "text-halo-width", 1.4); } catch (_e) {}
      }
    });
  } catch (_e) {
    // silencioso
  }
}

// Edificios 3D (fill-extrusion) usando la source vector del style (openmaptiles) --
function add3DBuildings(map) {
  try {
    const style = map.getStyle();
    if (!style || !style.sources) return;

    const sourceName = Object.keys(style.sources).find((k) => {
      const s = style.sources[k];
      return s && s.type === "vector";
    });
    if (!sourceName) return;

    if (map.getLayer("kiltro-3d-buildings")) return;

    const layers = style.layers || [];
    const labelLayerId = layers.find((l) => l.type === "symbol" && l.layout && l.layout["text-field"])?.id;

    // Ocultar la capa de buildings 2D original si existe (para que no se vea doble)
    layers.forEach((l) => {
      if (l.type === "fill" && l.id.toLowerCase().includes("building")) {
        try { map.setLayoutProperty(l.id, "visibility", "none"); } catch (_e) {}
      }
    });

    map.addLayer(
      {
        id: "kiltro-3d-buildings",
        source: sourceName,
        "source-layer": "building",
        type: "fill-extrusion",
        minzoom: 13,
        paint: {
          "fill-extrusion-color": [
            "interpolate", ["linear"], ["coalesce", ["get", "render_height"], ["get", "height"], 6],
            0, "#1C1C1C",
            15, "#222222",
            40, "#2A2A2A",
            90, "#353535",
            150, "#3E342A",
          ],
          "fill-extrusion-height": [
            "interpolate", ["linear"], ["zoom"],
            13, 0,
            14.5, ["coalesce", ["get", "render_height"], ["get", "height"], 6],
          ],
          "fill-extrusion-base": [
            "coalesce", ["get", "render_min_height"], ["get", "min_height"], 0,
          ],
          "fill-extrusion-opacity": 0.95,
        },
      },
      labelLayerId
    );
  } catch (_e) {
    // silencioso
  }
}

function markerHtml(label) {
  // Silueta HEAD-ONLY del Kiltro: PNG con fondo transparente (sólo el perro amarillo).
  // Sin círculo, sin fondo blanco. Sombra suave para que destaque sobre el mapa.
  const silhouette = `
    <img src="/kiltro-head.png" alt="Kiltro" style="
      width:100%; height:100%;
      display:block;
      object-fit:contain;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.9));
      pointer-events:none;
    " />`;

  const numberBadge = label != null
    ? `<div style="
        position:absolute; top:-4px; right:-6px;
        min-width:22px; height:22px; padding:0 6px;
        background:#000; color:${KILTRO_YELLOW};
        border:2px solid ${KILTRO_YELLOW};
        border-radius:999px;
        display:flex; align-items:center; justify-content:center;
        font-family:'Anton',Impact,sans-serif; font-size:13px; font-weight:900;
        box-shadow: 0 2px 6px rgba(0,0,0,0.85);
        z-index:2;
      ">${label}</div>`
    : "";

  return `
    <div class="kh-pin" style="position:relative; width:52px; height:52px; cursor:pointer; transform-origin: 50% 90%; transition: transform 0.18s ease;">
      ${silhouette}
      ${numberBadge}
    </div>
  `;
}

function esc(s) {
  if (!s) return "";
  return String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}

function popupHtml(v, index) {
  const rows = [];
  if (v.address) {
    // Usamos coords + (nombre) para que Google Maps muestre el local exacto
    // con su etiqueta. Formato: maps.google.com/maps?q=lat,lng+(Nombre+Local)
    // Esto evita el "denegado" del endpoint /maps/search?api=1 y siempre abre
    // el local correcto con su nombre como etiqueta del pin.
    const hasCoords = v.lat != null && v.lng != null;
    const labelEnc = encodeURIComponent(v.name || "");
    const mapsUrl = hasCoords
      ? `https://maps.google.com/maps?q=${v.lat},${v.lng}+(${labelEnc})`
      : `https://maps.google.com/maps?q=${encodeURIComponent(v.address + ", Santiago, Chile")}`;
    const addressInner = `<a href="${mapsUrl}" target="_blank" rel="noreferrer"
         style="color:#E5E5E5; font-size:12px; text-decoration:none; border-bottom:1px dashed ${KILTRO_YELLOW};">${esc(v.address)}</a>`;
    rows.push(`<div style="display:flex; gap:6px; align-items:flex-start; margin-top:8px;">
      <span style="color:${KILTRO_YELLOW}; font-size:12px;">📍</span>
      ${addressInner}
    </div>`);
  }
  const hours = formatHours(v.hours);
  const hoursBlock = hours
    ? `<div style="color:#A3A3A3; font-size:11px; line-height:1.5;">${hours}</div>`
    : `<div style="color:#7C7C7C; font-size:11px; font-style:italic;">${i18n.t("map.noHours")}</div>`;
  rows.push(`<div style="display:flex; gap:6px; align-items:flex-start; margin-top:6px;">
    <span style="color:${KILTRO_YELLOW}; font-size:12px;">🕒</span>
    ${hoursBlock}
  </div>`);
  const ratingSource = v.rating_source || "Google";
  const ratingRow = v.rating_avg
    ? `<div style="margin-top:6px;">
         <div style="color:${KILTRO_YELLOW}; font-size:13px; font-weight:700;">★ ${Number(v.rating_avg).toFixed(1)} <span style="color:#A3A3A3; font-size:10px; font-weight:400;">(${v.rating_count || 0} ${i18n.t("map.reviews")})</span></div>
         <div style="color:#7C7C7C; font-size:9.5px; font-family:'JetBrains Mono',monospace; text-transform:uppercase; letter-spacing:0.12em; margin-top:2px;">${i18n.t("map.source")} · ${esc(ratingSource)}</div>
       </div>`
    : "";

  return `
    <div style="font-family:'Outfit',sans-serif; color:#F5F5F5;">
      <div style="font-family:'Anton',Impact,sans-serif; font-size:20px; text-transform:uppercase; line-height:1;">
        ${index != null ? `${index}. ` : ""}${esc(v.name)}
      </div>
      <div style="font-family:'JetBrains Mono',monospace; font-size:10px; text-transform:uppercase; letter-spacing:0.12em; color:${KILTRO_YELLOW}; margin-top:4px;">
        ${esc(v.neighborhood || "")}
      </div>
      ${ratingRow}
      ${rows.join("")}
      <a href="/local/${esc(v.id)}"
         style="display:inline-block; margin-top:10px; color:${KILTRO_YELLOW}; font-family:'Anton',sans-serif; font-size:13px; text-transform:uppercase; letter-spacing:0.05em; text-decoration:none; border-bottom:1px solid ${KILTRO_YELLOW};">
        ${i18n.t("map.viewVenue")} →
      </a>
    </div>
  `;
}

function formatHours(hours) {
  if (!hours) return "";
  if (typeof hours === "string") return esc(hours);
  if (typeof hours === "object") {
    const dayNames = { mon: "Lun", tue: "Mar", wed: "Mié", thu: "Jue", fri: "Vie", sat: "Sáb", sun: "Dom" };
    const parts = [];
    ["mon", "tue", "wed", "thu", "fri", "sat", "sun"].forEach((k) => {
      const val = hours[k];
      if (val) parts.push(`${dayNames[k]}: ${esc(val)}`);
    });
    return parts.join("<br/>") || "";
  }
  return "";
}
